import produce from 'immer';
import { filter } from 'lodash';
import styled from 'styled-components';

import {
  FullSpanSubRowTD,
  TableSubRow,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/shared';
import MatchRequirementsView from 'dashboard/components-lib/MatchRequirementsView';
import {
  MatchRequirementKeys,
  MatchRequirementProfile,
} from 'dashboard/models/match-requirement-profile';
import {
  isMatchGroupCheck,
  isMatchGroupListCheck,
  MatchGroup,
  MatchGroupsCheckConfig,
  MatchRequirement,
  MatchRequirementsCheckConfig,
  MultipleCountryMatchGroup,
  SingleCountryMatchGroup,
} from 'dashboard/models/verification-check';
import { VerificationCheckConfig } from 'dashboard/models/verification-check-config/verification-check-config';
import { CountryCode } from 'lib/constants/locales';
import { CheckName } from 'lib/verificationCheck';

const CheckFullSpanSubRow = styled(FullSpanSubRowTD)`
  padding-right: 64px;
`;

const AccordionSectionContainer = styled.div`
  border-radius: ${({ theme }) => theme.element.borderRadius};
  border: 1px solid ${({ theme }) => theme.color.border.secondary};
`;

interface MatchRequirementsConfigurationElementProps {
  verificationTemplateVersionId: string;
  checkConfig: MatchRequirementsCheckConfig | MatchGroupsCheckConfig;
  selectedMatchRequirement: MatchRequirementKeys | null;
  matchRequirementProfiles: MatchRequirementProfile[];
  editable: boolean;
  handleUpdateCheck: (check: VerificationCheckConfig) => void;
  handleMatchRequirementSelected: (matchRequirementKeys: MatchRequirementKeys | null) => void;
}

const EMPTY_MATCH_REQUIREMENT: MatchRequirement = {
  name: '',
  description: '',
  'attributes-to-compare': [],
  'comparison-methods': [],
  'normalization-methods': [],
};

const MatchRequirementsConfigurationElement = ({
  verificationTemplateVersionId,
  checkConfig,
  selectedMatchRequirement,
  matchRequirementProfiles,
  editable,
  handleUpdateCheck,
  handleMatchRequirementSelected,
}: MatchRequirementsConfigurationElementProps) => {
  const checkName = checkConfig.check;
  const handleAddMatchRequirementToMatchGroup = (matchGroupIndex: number) => {
    const check = produce(checkConfig, (draftCheckConfig) => {
      if (isMatchGroupCheck(draftCheckConfig)) {
        draftCheckConfig['match-groups'][matchGroupIndex]['match-requirements'].push(
          EMPTY_MATCH_REQUIREMENT,
        );
      } else {
        draftCheckConfig['check-requirements'].push(EMPTY_MATCH_REQUIREMENT);
      }
    });
    handleUpdateCheck(check);
    handleMatchRequirementSelected({
      'match-group-index': matchGroupIndex,
      'match-requirement-index': isMatchGroupCheck(check)
        ? check['match-groups'][matchGroupIndex]['match-requirements'].length - 1
        : check['check-requirements'].length - 1,
      'object-id': verificationTemplateVersionId,
      scope: check.check,
    });
  };
  // Depending on their implementation, match groups have an array of acceptable country codes
  // or a single country code. We need to handle both cases
  const handleAddMatchGroup = (countryCode: CountryCode) => {
    if (!isMatchGroupCheck(checkConfig)) return;
    const isListInput = isMatchGroupListCheck(checkConfig);
    const check = produce(checkConfig, (draftCheckConfig) => {
      let checkConfig = {
        description: `Match group ${draftCheckConfig['match-groups'].length}`,
        name: `match_group_${draftCheckConfig['match-groups'].length}`,
        'match-requirements': [EMPTY_MATCH_REQUIREMENT],
      };
      if (isListInput) {
        checkConfig = {
          ...checkConfig,
          'country-codes': countryCode ? [countryCode] : ['default'],
        } as MultipleCountryMatchGroup;
      } else {
        checkConfig = {
          ...checkConfig,
          'country-code': countryCode ? countryCode : 'default',
        } as SingleCountryMatchGroup;
      }
      draftCheckConfig['match-groups'].push(checkConfig as MatchGroup);
    });

    handleUpdateCheck(check);
    handleMatchRequirementSelected({
      'match-group-index': check['match-groups'].length - 1,
      'match-requirement-index': 0,
      'object-id': verificationTemplateVersionId,
      scope: check.check,
    });
  };
  const handleDeleteMatchGroup = (matchGroupIndex: number) => {
    if (!isMatchGroupCheck(checkConfig)) return;
    // Dont let them delete the last one
    if (checkConfig['match-groups'].length <= 1) return;
    const check = produce(checkConfig, (draftCheckConfig) => {
      draftCheckConfig['match-groups'].splice(matchGroupIndex, 1);
    });
    handleUpdateCheck(check);
    // In the event that we delete the selected group, lets just nullify the selected match requirement
    if (
      selectedMatchRequirement &&
      selectedMatchRequirement['match-group-index'] === matchGroupIndex
    )
      handleMatchRequirementSelected(null);
  };
  const handleUpdateMatchGroupName = (matchGroupIndex: number, matchGroupName: string) => {
    if (!isMatchGroupCheck(checkConfig)) return;
    const check = produce(checkConfig, (draftCheckConfig) => {
      draftCheckConfig['match-groups'][matchGroupIndex].description = matchGroupName;
    });
    handleUpdateCheck(check);
  };
  const handleUpdateMatchGroupCountries = (matchGroupIndex: number, countries: CountryCode[]) => {
    if (!isMatchGroupCheck(checkConfig)) return;
    const check = produce(checkConfig, (draftCheckConfig) => {
      draftCheckConfig['match-groups'][matchGroupIndex] = {
        ...draftCheckConfig['match-groups'][matchGroupIndex],
        'country-codes': countries,
      };
    });
    handleUpdateCheck(check);
  };
  return (
    <TableSubRow>
      <CheckFullSpanSubRow colSpan={3} isLast={true}>
        <AccordionSectionContainer>
          <MatchRequirementsView
            matchGroups={
              isMatchGroupCheck(checkConfig)
                ? checkConfig['match-groups']
                : [
                    {
                      'country-code': 'default',
                      description: 'Match Group',
                      name: 'match_group',
                      'match-requirements': checkConfig['check-requirements'],
                    } as MatchGroup,
                  ]
            }
            usesMatchGroups={isMatchGroupCheck(checkConfig)}
            matchRequirementProfiles={filter(matchRequirementProfiles, { scope: checkName })}
            editable={editable}
            selectedMatchRequirement={selectedMatchRequirement}
            handleAddMatchRequirementToMatchGroup={handleAddMatchRequirementToMatchGroup}
            handleAddMatchGroup={handleAddMatchGroup}
            handleDeleteMatchGroup={handleDeleteMatchGroup}
            handleUpdateMatchGroupName={handleUpdateMatchGroupName}
            handleUpdateMatchGroupCountries={handleUpdateMatchGroupCountries}
            handleMatchRequirementSelected={(
              matchGroupIndex: number,
              matchRequirementIndex: number,
            ) =>
              handleMatchRequirementSelected({
                'match-group-index': matchGroupIndex,
                'match-requirement-index': matchRequirementIndex,
                'object-id': verificationTemplateVersionId,
                scope: checkName,
              })
            }
            // TODO: Jonny - Remove this once we have a way to configure the country code
            // Don't show it for DB Standard Identity just yet
            showCountryCode={
              isMatchGroupCheck(checkConfig) &&
              checkName !== CheckName.DatabaseStandardIdentityComparison
            }
          />
        </AccordionSectionContainer>
      </CheckFullSpanSubRow>
    </TableSubRow>
  );
};

export default MatchRequirementsConfigurationElement;
